// @ts-nocheck
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const BeheerPinContext = createContext(null);
const UNLOCK_MS = 10 * 60 * 1000;
const ITERATIONS = 120000;
const RESET_MS = 30 * 60 * 1000;

function bytesToBase64(bytes) {
  let s = "";
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s);
}
function base64ToBytes(s) {
  const raw = atob(s);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}
function bytesToHex(bytes) {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function sha256Hex(value) {
  const bits = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return bytesToHex(new Uint8Array(bits));
}
async function derivePinHash(pin, saltBytes) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", salt: saltBytes, iterations: ITERATIONS, hash: "SHA-256" }, key, 256);
  return bytesToBase64(new Uint8Array(bits));
}
function randomResetToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return bytesToBase64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function resetUrl(token) {
  const url = new URL(window.location.origin + "/");
  url.searchParams.set("pin_reset", token);
  return url.toString();
}
function verwijderResetParam() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("pin_reset");
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
  } catch {}
}

export function BeheerPinProvider({ config, onConfigSave, supabaseClient, session, userRole, children }) {
  const pinCfg = config?.modules?.__beheer_pin || null;
  const resetCfg = config?.modules?.__beheer_pin_reset || null;
  const pinConfigured = !!(pinCfg?.salt && pinCfg?.hash);
  const [unlockedUntil, setUnlockedUntil] = useState(0);
  const [prompt, setPrompt] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [pinFout, setPinFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const [herstelStatus, setHerstelStatus] = useState("");
  const [herstelFout, setHerstelFout] = useState("");
  const [herstelToegestaan, setHerstelToegestaan] = useState(false);
  const [herstelPin1, setHerstelPin1] = useState("");
  const [herstelPin2, setHerstelPin2] = useState("");
  const [herstelPinFout, setHerstelPinFout] = useState("");

  const isUnlocked = unlockedUntil > Date.now();
  const isOwner = userRole === "owner";
  const accountEmail = session?.user?.email || "";

  // Een gewijzigde PIN-config vergrendelt dit apparaat opnieuw zodra de nieuwe
  // tenantconfig binnenkomt. Daarmee blijven oude ontgrendelingen niet onbeperkt actief.
  useEffect(() => { setUnlockedUntil(0); }, [pinCfg?.hash]);

  const verify = async (pin) => {
    if (!pinConfigured) return true;
    try {
      const hash = await derivePinHash(pin, base64ToBytes(pinCfg.salt));
      return hash === pinCfg.hash;
    } catch {
      return false;
    }
  };

  const requireBeheer = async (actie = "deze wijziging") => {
    if (!pinConfigured || unlockedUntil > Date.now()) return true;
    return await new Promise((resolve) => {
      setPinInput("");
      setPinFout("");
      setHerstelStatus("");
      setHerstelFout("");
      setPrompt({ actie, resolve });
    });
  };

  const bevestigPrompt = async () => {
    if (!prompt || bezig) return;
    if (!/^\d{4}$/.test(pinInput)) { setPinFout("Voer de 4-cijferige beheer-pincode in."); return; }
    setBezig(true);
    const ok = await verify(pinInput);
    setBezig(false);
    if (!ok) { setPinFout("Onjuiste pincode."); return; }
    setUnlockedUntil(Date.now() + UNLOCK_MS);
    const r = prompt.resolve;
    setPrompt(null);
    setPinInput("");
    r(true);
  };

  const sluitPrompt = () => {
    if (!prompt) return;
    const r = prompt.resolve;
    setPrompt(null);
    setPinInput("");
    setPinFout("");
    setHerstelStatus("");
    setHerstelFout("");
    r(false);
  };

  const schrijfNieuwePin = async (pin, bevestiging, wisHerstel = false) => {
    if (!/^\d{4}$/.test(pin)) return { ok: false, fout: "Kies een pincode van precies 4 cijfers." };
    if (pin !== bevestiging) return { ok: false, fout: "De pincodes komen niet overeen." };
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const hash = await derivePinHash(pin, salt);
    const modules = { ...(config?.modules || {}), __beheer_pin: { salt: bytesToBase64(salt), hash, iterations: ITERATIONS } };
    if (wisHerstel) delete modules.__beheer_pin_reset;
    await onConfigSave?.({ ...config, modules });
    setUnlockedUntil(Date.now() + UNLOCK_MS);
    return { ok: true };
  };

  const setNieuwePin = async (pin, bevestiging) => schrijfNieuwePin(pin, bevestiging, false);

  const wijzigPin = async (pin, bevestiging) => {
    if (pinConfigured) {
      const ok = await requireBeheer("beheer-pincode wijzigen");
      if (!ok) return { ok: false, fout: "Niet ontgrendeld." };
    }
    return setNieuwePin(pin, bevestiging);
  };

  const disablePin = async () => {
    if (!pinConfigured) return true;
    const ok = await requireBeheer("beheer-pincode uitschakelen");
    if (!ok) return false;
    const modules = { ...(config?.modules || {}) };
    delete modules.__beheer_pin;
    delete modules.__beheer_pin_reset;
    await onConfigSave?.({ ...config, modules });
    setUnlockedUntil(0);
    return true;
  };

  const vraagPinHerstelAan = async () => {
    setHerstelFout("");
    setHerstelStatus("");
    if (!isOwner) { setHerstelFout("Alleen de eigenaar kan de beheer-pincode herstellen."); return false; }
    if (!supabaseClient || !accountEmail) { setHerstelFout("Je account kan nu niet worden geverifieerd."); return false; }
    setHerstelStatus("bezig");
    try {
      const token = randomResetToken();
      const tokenHash = await sha256Hex(token);
      const modules = {
        ...(config?.modules || {}),
        __beheer_pin_reset: {
          hash: tokenHash,
          expires_at: new Date(Date.now() + RESET_MS).toISOString(),
          email: accountEmail.toLowerCase(),
        },
      };
      await onConfigSave?.({ ...config, modules });
      const { error } = await supabaseClient.auth.signInWithOtp({
        email: accountEmail,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: resetUrl(token),
        },
      });
      if (error) {
        const schoon = { ...(config?.modules || {}) };
        delete schoon.__beheer_pin_reset;
        await onConfigSave?.({ ...config, modules: schoon });
        throw error;
      }
      setHerstelStatus("verzonden");
      return true;
    } catch (err) {
      setHerstelStatus("");
      setHerstelFout(err?.message || "Herstel-link versturen lukte niet.");
      return false;
    }
  };

  // Een herstel-link is pas geldig als de token uit de e-mail overeenkomt met
  // de tijdelijk opgeslagen hash én de ingelogde gebruiker de eigenaar is.
  useEffect(() => {
    let actief = true;
    (async () => {
      const token = new URLSearchParams(window.location.search).get("pin_reset");
      if (!token || !resetCfg?.hash || !session?.user) return;
      if (!isOwner) { if (actief) setHerstelPinFout("Alleen de eigenaar kan de pincode herstellen."); return; }
      if (resetCfg.email && accountEmail.toLowerCase() !== String(resetCfg.email).toLowerCase()) {
        if (actief) setHerstelPinFout("Deze herstel-link hoort bij een ander account.");
        return;
      }
      const geldigTot = Date.parse(resetCfg.expires_at || "");
      if (!geldigTot || geldigTot < Date.now()) {
        if (actief) setHerstelPinFout("Deze herstel-link is verlopen. Vraag een nieuwe link aan.");
        return;
      }
      const hash = await sha256Hex(token);
      if (!actief) return;
      if (hash !== resetCfg.hash) { setHerstelPinFout("Deze herstel-link is ongeldig."); return; }
      setHerstelToegestaan(true);
      setPrompt(null);
    })();
    return () => { actief = false; };
  }, [resetCfg?.hash, resetCfg?.expires_at, resetCfg?.email, session?.user?.id, userRole, accountEmail]);

  const bevestigHerstelPin = async () => {
    setHerstelPinFout("");
    setBezig(true);
    const r = await schrijfNieuwePin(herstelPin1, herstelPin2, true);
    setBezig(false);
    if (!r?.ok) { setHerstelPinFout(r?.fout || "Nieuwe pincode opslaan lukte niet."); return; }
    setHerstelPin1("");
    setHerstelPin2("");
    setHerstelToegestaan(false);
    verwijderResetParam();
  };

  const value = useMemo(() => ({
    pinConfigured,
    isUnlocked,
    unlockedUntil,
    requireBeheer,
    setNieuwePin,
    wijzigPin,
    disablePin,
    vraagPinHerstelAan,
    lockNow: () => setUnlockedUntil(0),
  }), [pinConfigured, isUnlocked, unlockedUntil, config, isOwner, accountEmail]);

  return (
    <BeheerPinContext.Provider value={value}>
      {children}
      {prompt && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={(e) => e.target === e.currentTarget && sluitPrompt()}>
          <div style={{ width: "100%", maxWidth: 360, background: "#fff", borderRadius: 16, padding: 22, boxShadow: "0 24px 60px rgba(15,23,42,.28)", fontFamily: "system-ui,-apple-system,sans-serif" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#0F172A", marginBottom: 5 }}>Beheer-pincode</div>
            <div style={{ fontSize: 12.5, color: "#64748B", lineHeight: 1.5, marginBottom: 16 }}>Voer de pincode in om {prompt.actie} uit te voeren. Beheer blijft daarna 10 minuten ontgrendeld.</div>
            <input autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={4} value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") bevestigPrompt(); if (e.key === "Escape") sluitPrompt(); }}
              placeholder="••••"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #CBD5E1", borderRadius: 10, padding: "12px 14px", fontSize: 24, fontWeight: 800, letterSpacing: 10, textAlign: "center", fontFamily: "inherit" }} />
            {pinFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginTop: 8 }}>{pinFout}</div>}
            {isOwner && herstelStatus !== "verzonden" && (
              <button type="button" onClick={vraagPinHerstelAan} disabled={herstelStatus === "bezig"}
                style={{ border: 0, background: "none", color: "#2563EB", padding: "9px 0 0", fontSize: 12.5, fontWeight: 700, cursor: herstelStatus === "bezig" ? "default" : "pointer" }}>
                {herstelStatus === "bezig" ? "Herstel-link versturen…" : "Pincode vergeten?"}
              </button>
            )}
            {herstelStatus === "verzonden" && <div style={{ marginTop: 10, padding: "9px 11px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, color: "#1D4ED8", fontSize: 12, lineHeight: 1.45 }}>Herstel-link verstuurd naar {accountEmail}. Open de link in de e-mail en kies daarna een nieuwe pincode.</div>}
            {herstelFout && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>{herstelFout}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button onClick={sluitPrompt} style={{ flex: 1, border: "1px solid #CBD5E1", background: "#fff", color: "#475569", borderRadius: 9, padding: 11, fontWeight: 700, cursor: "pointer" }}>Annuleren</button>
              <button onClick={bevestigPrompt} disabled={bezig} style={{ flex: 2, border: 0, background: "#2563EB", color: "#fff", borderRadius: 9, padding: 11, fontWeight: 700, cursor: bezig ? "default" : "pointer", opacity: bezig ? .7 : 1 }}>{bezig ? "Controleren…" : "Ontgrendelen"}</button>
            </div>
          </div>
        </div>
      )}
      {herstelToegestaan && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(15,23,42,.52)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 390, background: "#fff", borderRadius: 16, padding: 22, boxShadow: "0 24px 60px rgba(15,23,42,.28)", fontFamily: "system-ui,-apple-system,sans-serif" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 5 }}>Nieuwe beheer-pincode</div>
            <div style={{ fontSize: 12.5, color: "#64748B", lineHeight: 1.5, marginBottom: 16 }}>Je account is via de herstel-link bevestigd. Kies nu een nieuwe 4-cijferige pincode.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={4} value={herstelPin1} onChange={(e) => setHerstelPin1(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Nieuwe PIN" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #CBD5E1", borderRadius: 10, padding: "11px 12px", fontSize: 16, fontWeight: 700 }} />
              <input inputMode="numeric" pattern="[0-9]*" maxLength={4} value={herstelPin2} onChange={(e) => setHerstelPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Herhaal PIN" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #CBD5E1", borderRadius: 10, padding: "11px 12px", fontSize: 16, fontWeight: 700 }} />
            </div>
            {herstelPinFout && <div style={{ color: "#DC2626", fontSize: 12.5, marginTop: 9 }}>{herstelPinFout}</div>}
            <button onClick={bevestigHerstelPin} disabled={bezig} style={{ width: "100%", marginTop: 15, border: 0, background: "#2563EB", color: "#fff", borderRadius: 9, padding: 12, fontWeight: 700, cursor: bezig ? "default" : "pointer", opacity: bezig ? .7 : 1 }}>{bezig ? "Opslaan…" : "Nieuwe pincode opslaan"}</button>
          </div>
        </div>
      )}
    </BeheerPinContext.Provider>
  );
}

export function useBeheerPin() {
  const ctx = useContext(BeheerPinContext);
  if (!ctx) return {
    pinConfigured: false, isUnlocked: true, unlockedUntil: 0,
    requireBeheer: async () => true,
    setNieuwePin: async () => ({ ok: false, fout: "Beheer-pincode is niet beschikbaar." }),
    wijzigPin: async () => ({ ok: false, fout: "Beheer-pincode is niet beschikbaar." }),
    disablePin: async () => false,
    vraagPinHerstelAan: async () => false,
    lockNow: () => {},
  };
  return ctx;
}
